/**
 * Settings > Operating hours (PRD SETT-009, OPHRS-002, OPHRS-003).
 * Enable/Disable ipSCAPE polling + weekly schedule. Phase 2: UI; API + D1 app_config next.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

/** IANA time zone for interpreting open/close times (must match Worker Intl). */
const TIMEZONE_OPTIONS = [
  { value: 'Europe/London', label: 'UK (Europe/London)' },
  { value: 'Europe/Dublin', label: 'Ireland (Europe/Dublin)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Paris', label: 'Central Europe (Europe/Paris)' },
  { value: 'America/New_York', label: 'US Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'US Central (America/Chicago)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (America/Los_Angeles)' },
  { value: 'Australia/Sydney', label: 'Australia Eastern (Australia/Sydney)' },
] as const;

/** D1 stores app_config values as TEXT; API returns strings like "true" / "false". */
function parseAppConfigBool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes';
  }
  return false;
}

export default function SettingsOperatingHours() {
  const { getToken } = useAuth();
  const [ipscapeEnabled, setIpscapeEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<Record<string, { open: string; close: string; closed: boolean }>>(
    DAYS.reduce((acc, d) => ({ ...acc, [d]: { open: '09:00', close: '17:00', closed: d === 'Sunday' } }), {} as Record<string, { open: string; close: string; closed: boolean }>)
  );
  const [operatingTimezone, setOperatingTimezone] = useState('Europe/London');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      try {
        const base = typeof window !== 'undefined' ? window.location.origin : '';
        const [configRes] = await Promise.all([
          fetch(`${base}/api/config?keys=ipscape_enabled,operating_hours,operating_hours_timezone`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (configRes.ok) {
          const data = await configRes.json();
          if (data.ipscape_enabled !== undefined) setIpscapeEnabled(parseAppConfigBool(data.ipscape_enabled));
          if (data.operating_hours && typeof data.operating_hours === 'object') {
            setSchedule((prev) => ({ ...prev, ...data.operating_hours }));
          }
          if (typeof data.operating_hours_timezone === 'string' && data.operating_hours_timezone.trim()) {
            setOperatingTimezone(data.operating_hours_timezone.trim());
          }
        }
      } catch {
        // API may not exist yet; keep defaults
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const apiBase = typeof window !== 'undefined' ? window.location.origin : '';

  const saveConfig = async (payload: {
    ipscape_enabled?: boolean;
    operating_hours?: Record<string, { open: string; close: string; closed: boolean }>;
    operating_hours_timezone?: string;
  }) => {
    const token = await getToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const wakePresenceWorker = async () => {
    const token = await getToken();
    if (!token) return;
    try {
      const res = await fetch(`${apiBase}/api/config?keys=presence_ws_url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as { presence_ws_url?: string };
      const ws = data.presence_ws_url?.trim();
      if (!ws) return;
      const origin = ws.replace(/^wss:/i, 'https:').replace(/\/ws\/?$/i, '');
      await fetch(`${origin}/presence/test-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bypassEnabled: false, bypassSchedule: false }),
      });
    } catch {
      /* non-fatal — alarm will pick up within poll interval */
    }
  };

  const handleToggle = async () => {
    const next = !ipscapeEnabled;
    try {
      await saveConfig({ ipscape_enabled: next });
      setIpscapeEnabled(next);
      if (next) await wakePresenceWorker();
    } catch {
      /* saveConfig already setError */
    }
  };

  const handleSaveSchedule = async () => {
    await saveConfig({ operating_hours: schedule, operating_hours_timezone: operatingTimezone });
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Operating hours</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
        When the call centre is open, ipSCAPE polling (agent status, campaign stats) runs. When closed or disabled, polling stops.
      </p>

      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={ipscapeEnabled}
            onChange={handleToggle}
            disabled={loading || saving}
          />
          <span>Enable ipSCAPE polling</span>
        </label>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '0.5rem', marginLeft: '1.75rem' }}>
          When disabled, the worker does not call the UC API. A slow check runs about once a minute so polling can
          resume when you turn this back on. The weekly schedule is ignored while disabled.
        </p>
        {error && <p style={{ color: 'var(--error)', marginTop: '0.5rem' }}>{error}</p>}
      </div>

      <h3 style={{ marginBottom: '0.75rem' }}>Weekly schedule</h3>
      <p style={{ color: 'var(--text-muted)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
        Open/close times use the selected IANA time zone (default UK).         Outside these hours the worker sleeps until the next open time (no UC requests and no poll log spam). The
        Status page shows a closed snapshot instead of live colours. During open hours, Platform{' '}
        <code style={{ fontSize: '0.8em' }}>GET /v3.0/users</code> runs about every 5 minutes for authoritative{' '}
        <code style={{ fontSize: '0.8em' }}>isLoggedIn</code> (used by the logged-in-only filter on Status).
      </p>
      <div style={{ marginBottom: '1.25rem' }}>
        <label style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
          Time zone for open/close times
        </label>
        <select
          value={operatingTimezone}
          onChange={(e) => setOperatingTimezone(e.target.value)}
          disabled={loading || saving}
          style={{
            minWidth: 280,
            padding: '0.45rem 0.65rem',
            borderRadius: 6,
            border: '1px solid var(--widget-bg)',
            background: 'var(--bg)',
            color: 'var(--text)',
            fontSize: '0.875rem',
          }}
        >
          {!TIMEZONE_OPTIONS.some((o) => o.value === operatingTimezone) && (
            <option value={operatingTimezone}>{operatingTimezone} (saved)</option>
          )}
          {TIMEZONE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {DAYS.map((day) => (
          <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <span style={{ width: '100px' }}>{day}</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
              <input
                type="checkbox"
                checked={!schedule[day]?.closed}
                onChange={(e) =>
                  setSchedule((s) => ({
                    ...s,
                    [day]: { ...s[day], closed: !e.target.checked, open: s[day]?.open ?? '09:00', close: s[day]?.close ?? '17:00' },
                  }))
                }
              />
              Open
            </label>
            {!schedule[day]?.closed && (
              <>
                <input
                  type="time"
                  value={schedule[day]?.open ?? '09:00'}
                  onChange={(e) => setSchedule((s) => ({ ...s, [day]: { ...s[day], open: e.target.value, close: s[day]?.close ?? '17:00', closed: false } }))}
                  style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid var(--widget-bg)' }}
                />
                <span style={{ color: 'var(--text-muted)' }}>–</span>
                <input
                  type="time"
                  value={schedule[day]?.close ?? '17:00'}
                  onChange={(e) => setSchedule((s) => ({ ...s, [day]: { ...s[day], close: e.target.value, open: s[day]?.open ?? '09:00', closed: false } }))}
                  style={{ padding: '0.25rem', borderRadius: '4px', border: '1px solid var(--widget-bg)' }}
                />
              </>
            )}
          </div>
        ))}
      </div>
      <p style={{ marginTop: '1rem' }}>
        <button
          type="button"
          onClick={handleSaveSchedule}
          disabled={loading || saving}
          style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: loading || saving ? 'not-allowed' : 'pointer' }}
        >
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
      </p>
    </div>
  );
}
